import React, { useState, useEffect } from "react";
import { hermesApi } from "../../lib/api";
import { toast } from "sonner";
import { FloppyDisk, Rocket, ClockCounterClockwise, ArrowCounterClockwise, Eye } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";

function VersionItem({ v, onView, onRollback }) {
  return (
    <div className="flex items-start gap-2 py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-[12px] text-slate-700 font-medium truncate">
          {v.change_note || "Updated"}
        </div>
        <div className="text-[11px] font-mono text-slate-400 mt-0.5">
          {new Date(v.created_at).toLocaleString("en-AU", {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
          })}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button
          onClick={() => onView(v)}
          className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
          title="View this version"
        >
          <Eye size={13} />
        </button>
        <button
          onClick={() => onRollback(v)}
          className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-amber-600 transition-colors"
          title="Roll back to this version"
        >
          <ArrowCounterClockwise size={13} />
        </button>
      </div>
    </div>
  );
}

export default function IdentityPage() {
  const FILE = "SOUL.md";
  const [content,    setContent]    = useState("");
  const [original,   setOriginal]   = useState("");
  const [versions,   setVersions]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [note,       setNote]       = useState("");
  const [viewVer,    setViewVer]    = useState(null); // {content, ...}
  const [viewLoading,setViewLoading]= useState(false);

  useEffect(() => {
    hermesApi.config.get(FILE)
      .then(({ data }) => {
        setContent(data.content);
        setOriginal(data.content);
        setVersions(data.versions || []);
      })
      .catch(() => toast.error("Failed to load SOUL.md"))
      .finally(() => setLoading(false));
  }, []);

  const isDirty = content !== original;

  const save = async (activate) => {
    if (!note.trim() && activate) {
      toast.error("Add a change note before activating");
      return;
    }
    setSaving(true);
    try {
      await hermesApi.config.save(FILE, {
        content,
        change_note: note || "Draft saved",
        activate,
      });
      setOriginal(activate ? content : original);
      setNote("");
      // Refresh versions
      const { data } = await hermesApi.config.get(FILE);
      setVersions(data.versions || []);
      toast.success(activate ? "SOUL.md activated — Hermes will use this on next run" : "Version saved as draft");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const viewVersion = async (v) => {
    setViewLoading(true);
    try {
      const { data } = await hermesApi.config.version(FILE, v.id);
      setViewVer(data);
    } catch { toast.error("Failed to load version"); }
    finally { setViewLoading(false); }
  };

  const rollback = async (v) => {
    if (!window.confirm(`Roll back to version from ${new Date(v.created_at).toLocaleString("en-AU")}?\n\nThis will overwrite the current SOUL.md and activate immediately.`)) return;
    setSaving(true);
    try {
      await hermesApi.config.rollback(FILE, v.id);
      const { data } = await hermesApi.config.get(FILE);
      setContent(data.content);
      setOriginal(data.content);
      setVersions(data.versions || []);
      setViewVer(null);
      toast.success("Rolled back and activated");
    } catch { toast.error("Rollback failed"); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full text-[13px] text-slate-400">Loading SOUL.md…</div>
  );

  return (
    <div className="flex h-full">
      {/* Editor */}
      <div className="flex-1 flex flex-col min-w-0 border-r border-slate-200">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1">Hermes Governance</p>
              <h1 className="font-display text-[22px] font-bold text-slate-900 tracking-tight">Identity</h1>
              <p className="text-[13px] text-slate-500 mt-1">
                SOUL.md — Hermes's mission, role, operating principles, and behavioral constraints.
              </p>
            </div>
            {isDirty && (
              <span className="text-[11px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded shrink-0">
                Unsaved changes
              </span>
            )}
          </div>
        </div>

        {/* Textarea */}
        <div className="flex-1 min-h-0 relative">
          {viewVer ? (
            <div className="absolute inset-0 flex flex-col bg-slate-50">
              <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center gap-3 shrink-0">
                <span className="text-[12px] font-semibold text-amber-600">Viewing version from {new Date(viewVer.created_at).toLocaleString("en-AU")}</span>
                <span className="text-[12px] text-slate-500">{viewVer.change_note}</span>
                <Button variant="outline" size="sm" className="ml-auto" onClick={() => setViewVer(null)}>
                  Back to current
                </Button>
                <Button variant="outline" size="sm" onClick={() => rollback(viewVer)}>
                  <ArrowCounterClockwise size={12} /> Restore this
                </Button>
              </div>
              <textarea
                readOnly
                value={viewVer.content}
                className="flex-1 font-mono text-[13px] text-slate-700 bg-slate-50 px-6 py-4 resize-none focus:outline-none leading-relaxed"
              />
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="absolute inset-0 w-full h-full font-mono text-[13px] text-slate-800 bg-white px-6 py-4 resize-none focus:outline-none leading-relaxed border-0"
              spellCheck={false}
              placeholder="SOUL.md content…"
            />
          )}
        </div>

        {/* Save bar */}
        {!viewVer && (
          <div className="border-t border-slate-200 px-6 py-3 bg-white shrink-0 flex items-center gap-3">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Change note (required to activate)…"
              className="flex-1 text-[13px] border border-slate-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-200 placeholder:text-slate-400"
            />
            <Button variant="outline" size="sm" onClick={() => save(false)} disabled={saving || !isDirty}>
              <FloppyDisk size={13} /> Save draft
            </Button>
            <Button size="sm" onClick={() => save(true)} disabled={saving || !isDirty}
              className="bg-indigo-600 hover:bg-indigo-700 text-white border-0">
              <Rocket size={13} /> Activate
            </Button>
            <span className="text-[11px] font-mono text-slate-400 ml-2">
              {content.length.toLocaleString()} chars
            </span>
          </div>
        )}
      </div>

      {/* Version history */}
      <div className="w-72 shrink-0 flex flex-col bg-white">
        <div className="px-4 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={14} className="text-slate-400" />
            <span className="text-[13px] font-semibold text-slate-700">Version History</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Every save creates a version. Click to preview, roll back to restore.</p>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {versions.length === 0 ? (
            <p className="text-[12px] text-slate-400 text-center py-8">No versions yet. Save a draft to start tracking.</p>
          ) : (
            versions.map((v) => (
              <VersionItem key={v.id} v={v} onView={viewVersion} onRollback={rollback} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
